import {
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const SUPPORT_ARTICLE_URL =
  "https://calebandbrown.com/en-gb/uk-cryptoasset-investing";

export type UKInvestorStatementType = "restricted" | "hnw";

interface InvestorQuestionConfig {
  label: string;
  adornment: "%" | "£";
  // Increment applied by the number input's spinner arrows.
  step: number;
}

const QUESTIONS: Record<UKInvestorStatementType, [InvestorQuestionConfig, InvestorQuestionConfig]> = {
  restricted: [
    {
      label:
        "Over the last 12 months, roughly what percentage of your net assets have you invested in high-risk investments?",
      adornment: "%",
      step: 1,
    },
    {
      label:
        "In the next 12 months, roughly what percentage of your net assets do you intend to invest in high-risk investments?",
      adornment: "%",
      step: 1,
    },
  ],
  hnw: [
    {
      label: "Please specify your income to the nearest £10k in the last financial year",
      adornment: "£",
      step: 10_000,
    },
    {
      label: "Please specify your net assets to the nearest £100k in the last financial year",
      adornment: "£",
      step: 100_000,
    },
  ],
};

const RESTRICTED_MAX_PERCENTAGE = 10;
const HNW_MIN_INCOME = 100_000;
const HNW_MIN_NET_ASSETS = 250_000;

const FAIL_MESSAGE =
  "You have indicated that you do not meet the criteria to trade cryptoassets";

function meetsCriteria(
  investorType: UKInvestorStatementType,
  firstAnswer: number,
  secondAnswer: number,
): boolean {
  if (investorType === "restricted") {
    return (
      firstAnswer <= RESTRICTED_MAX_PERCENTAGE && secondAnswer <= RESTRICTED_MAX_PERCENTAGE
    );
  }
  // HNW qualification only requires one of the two criteria to be met.
  return firstAnswer >= HNW_MIN_INCOME || secondAnswer >= HNW_MIN_NET_ASSETS;
}

interface UKAppropriatenessWarningDialogProps {
  open: boolean;
  isLoading: boolean;
  investorType: UKInvestorStatementType;
  onClose: () => void;
  onProceed: () => void;
  // Called when the user fails the eligibility questions and clicks Exit; the
  // parent is expected to navigate back to the Investor Classification page.
  onExit: () => void;
}

export default function UKAppropriatenessWarningDialog({
  open,
  isLoading,
  investorType,
  onClose,
  onProceed,
  onExit,
}: UKAppropriatenessWarningDialogProps) {
  const [step, setStep] = useState<"questions" | "failed" | "warning">("questions");
  const [firstAnswer, setFirstAnswer] = useState("");
  const [secondAnswer, setSecondAnswer] = useState("");

  useEffect(() => {
    if (open) {
      setStep("questions");
      setFirstAnswer("");
      setSecondAnswer("");
    }
  }, [open]);

  const [firstQuestion, secondQuestion] = QUESTIONS[investorType];

  const firstValue = Number(firstAnswer);
  const secondValue = Number(secondAnswer);
  const answersComplete =
    firstAnswer.trim() !== "" &&
    secondAnswer.trim() !== "" &&
    Number.isFinite(firstValue) &&
    Number.isFinite(secondValue) &&
    firstValue >= 0 &&
    secondValue >= 0;

  const handleNext = () => {
    if (!answersComplete) return;
    setStep(meetsCriteria(investorType, firstValue, secondValue) ? "warning" : "failed");
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isLoading) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ p: 4 }}>
        {step === "questions" && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={700} textAlign="center">
              Before you continue
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body1">{firstQuestion.label}</Typography>
              <TextField
                fullWidth
                type="number"
                value={firstAnswer}
                onChange={(e) => setFirstAnswer(e.target.value)}
                inputProps={{ min: 0, step: firstQuestion.step }}
                InputProps={
                  firstQuestion.adornment === "£"
                    ? { startAdornment: <InputAdornment position="start">£</InputAdornment> }
                    : { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                }
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body1">{secondQuestion.label}</Typography>
              <TextField
                fullWidth
                type="number"
                value={secondAnswer}
                onChange={(e) => setSecondAnswer(e.target.value)}
                inputProps={{ min: 0, step: secondQuestion.step }}
                InputProps={
                  secondQuestion.adornment === "£"
                    ? { startAdornment: <InputAdornment position="start">£</InputAdornment> }
                    : { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                }
              />
            </Stack>
            <Stack direction="row" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleNext}
                disabled={!answersComplete}
                sx={{ minWidth: 200 }}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        )}

        {step === "failed" && (
          <Stack spacing={3} textAlign="center">
            <Typography variant="h6" fontWeight={700} color="error">
              {FAIL_MESSAGE}
            </Typography>
            <Stack direction="row" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onExit}
                sx={{ minWidth: 200 }}
              >
                Exit
              </Button>
            </Stack>
          </Stack>
        )}

        {step === "warning" && (
          <Stack spacing={2} textAlign="center">
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ textDecoration: "underline" }}
            >
              WARNING: PLEASE READ
            </Typography>
            <Typography variant="body1">
              You are about to commence the appropriateness test. You must pass this test in
              order to successfully become a Caleb &amp; Brown client.
            </Typography>
            <Typography variant="body1">
              You will have three attempts to pass the test, so please take your time, read
              each question carefully and consider your answers before submitting.
            </Typography>
            <Typography variant="body1">
              If you fail the test three times,{" "}
              <strong>Caleb &amp; Brown will not be able to onboard you as a client.</strong>
            </Typography>
            <Typography variant="body1">
              Caleb &amp; Brown brokers and support staff cannot assist you with answering the
              test questions.
            </Typography>
            <Typography variant="body1">
              Before starting, we recommend reviewing our support article on crypto markets
              and the UK regulatory environment here:{" "}
              <Link
                href={SUPPORT_ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ wordBreak: "break-word" }}
              >
                {SUPPORT_ARTICLE_URL}
              </Link>
            </Typography>
            <Stack direction="row" justifyContent="center" mt={1}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onProceed}
                disabled={isLoading}
                sx={{ minWidth: 200 }}
              >
                {isLoading ? "Submitting..." : "Proceed"}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
